import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent, HttpResponse, HttpErrorResponse, HttpClient } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { Observable, throwError, BehaviorSubject, EMPTY } from 'rxjs';
import { tap, catchError, switchMap, filter, take, timeout } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie';
import { AUTH_INTERCEPTOR_CONFIG, AuthInterceptorConfig } from '../tokens/auth-config.token';
import { AmplitudeService } from '../../services/amplitude/amplitude.service';

// Module-level state for token refresh coordination
// These are shared across all interceptor calls to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

// Token expiry constant (365 days in milliseconds)
const REFRESH_TOKEN_EXPIRY_MS = 365 * 24 * 60 * 60 * 1000;

/**
 * Authentication interceptor that handles:
 * - Bearer token injection
 * - Basic auth for login requests
 * - Content-Type headers
 * - Error logging with app name
 * - 401 redirect handling
 */
export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const config = inject(AUTH_INTERCEPTOR_CONFIG);
  const cookieService = inject(CookieService);
  const router = inject(Router);
  const platformId = inject(PLATFORM_ID);
  const isBrowser = isPlatformBrowser(platformId);
  const amplitudeService = inject(AmplitudeService);
  const http = inject(HttpClient);
  const loginUrlPattern = config.loginUrlPattern || 'way-auth/auth/login';
  const insurancePathPatterns = config.insurancePathPatterns || ['/insurance-steps', '/auto-insurance', '/ai', '/insurance-pay'];
  const skipAuthPatterns = config.skipAuthUrlPatterns ?? ['way-images.s3', 'ui-config.json'];

  // Requests to these URLs must not get Basic/Bearer (e.g. S3 public GET)
  const urlLower = req.url.toLowerCase();
  if (skipAuthPatterns.some(p => urlLower.includes(p.toLowerCase()))) {
    return next(req);
  }

  // Check if this is a login request
  let isLoginUrl = false;
  if (req.url.toLowerCase().indexOf(loginUrlPattern.toLowerCase()) > 0) {
    cookieService.remove('Bearer');
    isLoginUrl = true;
  }

  const bearerRaw =  isBrowser ? cookieService.get('Bearer') : null;
  // Treat string 'undefined' as no token (handles legacy bad cookies)
  const bearer = bearerRaw && bearerRaw !== 'undefined' ? bearerRaw : null;
  let modifiedReq: HttpRequest<unknown> = req;

  if (bearer) {
    // User is authenticated - add Bearer token
    modifiedReq = addBearerToken(req, bearer);
  } else {
    // User not authenticated - add Basic auth
    modifiedReq = addBasicAuth(req, config, isLoginUrl);
  }

  // Handle special header cases
  modifiedReq = handleSpecialHeaders(req, modifiedReq, bearer, config, isBrowser);

  return next(modifiedReq).pipe(
    tap((event: HttpEvent<unknown>) => {
      if (event instanceof HttpResponse && !bearer && isBrowser) {
        // Extract and store access token from response headers
        const accessToken = event.headers.get('access_token');
        const refreshToken = event.headers.get('refresh_token');
        // Create separate expiry for refresh token (365 days)
        const refreshTokenExpiry = new Date();
        refreshTokenExpiry.setTime(refreshTokenExpiry.getTime() + REFRESH_TOKEN_EXPIRY_MS);
        // Only store valid tokens (not null, undefined, or the string 'undefined')
        if (accessToken && accessToken !== 'undefined' && accessToken.trim() !== '') {
          sessionStorage.setItem('Bearer', accessToken);
          cookieService.put('Bearer', accessToken, {
              expires: refreshTokenExpiry,
              path: '/',
              secure: true
          });
        }
        if (refreshToken && refreshToken !== 'undefined' && refreshToken.trim() !== '') {
          cookieService.put('RefreshToken', refreshToken, {
            expires: refreshTokenExpiry,
            path: '/',
            secure: true
          });
        }
        localStorage['serverCurrentDate'] = event.headers.get('Date');
      }
    }),
    catchError((err: HttpErrorResponse) => {
      if (err instanceof HttpErrorResponse) {
        if (err.status === 401) {
          // Check if this request is already a token refresh attempt
          const isRefreshRequest = req.headers.get('skip-refresh') === 'true';
          
          if (!isRefreshRequest) {
            // Attempt token refresh
            return handleUnauthorized(
              err,
              req,
              next,
              config,
              cookieService,
              router,
              isBrowser,
              insurancePathPatterns,
              http
            );
          } else {
            // Refresh token itself failed - force logout
            handleFinalAuthFailure(cookieService, router, isBrowser, insurancePathPatterns);
            return throwError(() => err);
          }
        } else if (isServerOrClientError(err.status)) {
          // logError(err, config, isBrowser);
        }
        if (isBrowser) {
          try {
            let eventProperties = {
              'api_name': req.url,
              'request_params': JSON.stringify(req.body),
              'status_code': err.status,
              'error_message': JSON.stringify(err.error),
              'user_agent': navigator.userAgent,
              'user_id': localStorage.getItem('userId') || '',
              'user_email': localStorage.getItem('userEmail') || '',
            };
            amplitudeService.amplitudeFunction('Web_API_Failure', eventProperties, 'Web');
          } catch (exp) { }
        }
      }
      return throwError(() => err);
    })
  );
};

/**
 * Multipart / file uploads: must not force `Content-Type: application/json` (drops multipart boundary;
 * Spring then treats the body as `application/octet-stream` → 415).
 */
function isMultipartLikeRequest(req: HttpRequest<unknown>): boolean {
  if (req.headers.get('tp') === 'multipart/form-data') {
    return true;
  }
  if (typeof FormData !== 'undefined' && req.body instanceof FormData) {
    return true;
  }
  const ct = (req.headers.get('Content-Type') ?? '').toLowerCase();
  return ct.startsWith('multipart/form-data');
}

/**
 * Add Bearer token to request
 */
function addBearerToken(req: HttpRequest<unknown>, bearer: string): HttpRequest<unknown> {
  if (isMultipartLikeRequest(req)) {
    return req.clone({
      setHeaders: { 'authorization': 'Bearer ' + bearer }
    });
  } else if (req.method === 'PUT' || req.method === 'POST'  || req.method == 'PATCH' || req.method == 'DELETE') {
    return req.clone({
      setHeaders: {
        'authorization': 'Bearer ' + bearer,
        'Content-Type': 'application/json;charset=UTF-8'
      }
    });
  } else if (req.method === 'GET') {
    return req.clone({
      setHeaders: { 'authorization': 'Bearer ' + bearer }
    });
  } else {
    return req.clone({
      setHeaders: {
        'authorization': 'Bearer ' + bearer,
        'Content-Type': 'application/json;'
      }
    });
  }
}

/**
 * Add Basic auth for unauthenticated requests
 */
function addBasicAuth(req: HttpRequest<unknown>, config: AuthInterceptorConfig, isLoginUrl: boolean): HttpRequest<unknown> {
  const authData = btoa(config.authUsername + ':' + config.authPassword);
  
  if (isLoginUrl) {
    return req.clone({
      setHeaders: {
        'method': req.method,
        'Authorization': 'Basic ' + authData,
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
  } else if (req.url.indexOf('document/public/upload') > -1) {
    return req.clone({
      setHeaders: {
        'method': req.method,
        'Authorization': 'Basic ' + authData
      }
    });
  } else if (isMultipartLikeRequest(req)) {
    // Do not set Content-Type — HttpClient must set multipart boundary on FormData bodies.
    return req.clone({
      setHeaders: {
        'method': req.method,
        'Authorization': 'Basic ' + authData,
      },
    });
  } else if (req.method === 'GET') {
    return req.clone({
      setHeaders: {
        'method': req.method,
        'Authorization': 'Basic ' + authData
      }
    });
  } else {
    return req.clone({
      setHeaders: {
        'method': req.method,
        'Authorization': 'Basic ' + authData,
        'Content-Type': 'application/json;charset=UTF-8'
      }
    });
  }
}

/**
 * Handle special header cases (setbearerSeparate, setbearer)
 */
function handleSpecialHeaders(originalReq: HttpRequest<unknown>, modifiedReq: HttpRequest<unknown>, bearer: string | null, config: AuthInterceptorConfig, isBrowser: boolean): HttpRequest<unknown> {

  if (isBrowser && (originalReq.url.toLowerCase().indexOf('way-insurance/') > 0 || originalReq.url.toLowerCase().indexOf('mobile-refinance/') > 0) && localStorage['App'] && localStorage['AppVersion'] && localStorage['AppOS'] && localStorage['AppHardware']) {
    return originalReq.clone({
      setHeaders: {
        'method': originalReq.method,
        'Authorization': bearer ? 'Bearer ' + bearer : 'Basic ' + btoa(config.authUsername + ':' + config.authPassword),
        'User-Agent-App': localStorage['App'],
        'User-Agent-App-Version': localStorage['AppVersion'],
        'User-Agent-OS-Version': localStorage['AppOS'],
        'User-Agent-Hardware': localStorage['AppHardware'],
        'User-Agent-DeviceType': localStorage['AppDeviceType'],
      }
    });
  }

  if (originalReq.headers.get('setbearerSeparate') === 'separate-bearer') {
    if (originalReq.method === 'GET' || originalReq.method === 'POST') {
      const separateBearerVal = originalReq.headers.get('setbearerSeparateVal') || '';
      return originalReq.clone({
        setHeaders: {
          'method': originalReq.method,
          'Authorization': separateBearerVal,
          'Content-Type': 'application/json;charset=UTF-8'
        }
      });
    } else {
      return originalReq.clone({ setHeaders: {} });
    }
  }

  if (originalReq.headers.get('setbearer') === 'no-bearer') {
    if (
      (originalReq.method === 'PUT' || originalReq.method === 'POST') &&
      !isMultipartLikeRequest(originalReq)
    ) {
      return originalReq.clone({
        setHeaders: { 'Content-Type': 'application/json;charset=UTF-8' }
      });
    } else {
      return originalReq.clone({ setHeaders: {} });
    }
  }

  return modifiedReq;
}

/**
 * Handle 401 Unauthorized errors by attempting token refresh
 * Returns an Observable to allow retrying the original request with new token
 */
function handleUnauthorized(
  err: HttpErrorResponse,
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  config: AuthInterceptorConfig,
  cookieService: CookieService,
  router: Router,
  isBrowser: boolean,
  insurancePathPatterns: string[],
  http: HttpClient
): Observable<HttpEvent<unknown>> {
  
  if (!isBrowser) {
    return throwError(() => err);
  }

  // Check if we're already refreshing the token
  if (!isRefreshing) {
    // We're the first 401, so we'll handle the refresh
    isRefreshing = true;
    refreshTokenSubject.next(null);

    // Get refresh token from cookies
    const refreshToken =  isBrowser ? cookieService.get('RefreshToken') : null;
    
    if (!refreshToken) {
      // No refresh token available - force logout
      isRefreshing = false;
      handleFinalAuthFailure(cookieService, router, isBrowser, insurancePathPatterns);
      return throwError(() => err);
    }

    // Prepare the refresh token request
    const formData = new URLSearchParams();
    formData.set('grant_type', 'refresh_token');
    formData.set('refresh_token', refreshToken);
    formData.set('scope', 'way');

    // Make the refresh token API call
    return http.post<any>(
      '/way-auth/auth/login',
      formData.toString(),
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'skip-refresh': 'true'  // Prevent infinite loop if this fails
        }
      }
    ).pipe(
      switchMap((res: any) => {
        // Refresh successful!
        isRefreshing = false;
        const newAccessToken = res.access_token;
        
        // Notify all waiting requests of the new token
        refreshTokenSubject.next(newAccessToken);

        // Calculate expiry (365 days)
        const refreshTokenExpiry = new Date();
        refreshTokenExpiry.setTime(
          refreshTokenExpiry.getTime() + REFRESH_TOKEN_EXPIRY_MS
        );

        // Store the new tokens
        if (newAccessToken && newAccessToken !== '' && newAccessToken !== null) {
          sessionStorage.setItem('Bearer', newAccessToken);
          cookieService.put('Bearer', newAccessToken, {
            expires: refreshTokenExpiry,
            path: '/',
            secure: true
          });
        }

        if (res.refresh_token && res.refresh_token !== '' && res.refresh_token !== null) {
          cookieService.put('RefreshToken', res.refresh_token, {
            expires: refreshTokenExpiry,
            path: '/',
            secure: true
          });
        }

        // Retry the original request with the new token
        return next(
          req.clone({
            setHeaders: {
              Authorization: `Bearer ${newAccessToken}`
            }
          })
        );
      }),
      catchError((refreshErr) => {
        // Refresh failed - force logout
        isRefreshing = false;
        handleFinalAuthFailure(cookieService, router, isBrowser, insurancePathPatterns);
        return throwError(() => refreshErr);
      })
    );
  } else {
    // Another request is already refreshing the token
    // Wait for it to complete and then retry with the new token
    return refreshTokenSubject.pipe(
      filter(token => token !== null),  // Wait until we have a token
      take(1),                           // Take only the first emission
      switchMap(token => {
        // Retry the original request with the new token
        return next(
          req.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`
            }
          })
        );
      }),
      catchError(queueErr => {
        // Timeout or other error while waiting
        console.error('Token refresh queue error:', queueErr);
        if (isBrowser) {
          // Reload the page as fallback
          window.location.href = window.location.href;
        }
        return EMPTY;
      })
    );
  }
}

/**
 * Handle final authentication failure - clear tokens and redirect to login
 */
function handleFinalAuthFailure(
  cookieService: CookieService,
  router: Router,
  isBrowser: boolean,
  insurancePathPatterns: string[]
): void {
  if (!isBrowser) return;

  // Clear all auth tokens
  cookieService.remove('Bearer');
  cookieService.remove('RefreshToken');
  sessionStorage.removeItem('Bearer');

  const currentPath = window.location.pathname;
  
  // Check if we're on an insurance path
  const isInsurancePath = insurancePathPatterns.some(pattern => currentPath.includes(pattern));

  if (isInsurancePath) {
    // For insurance paths, reload the page instead of redirecting
    setTimeout(() => window.location.reload(), 500);
  } else {
    // For other paths, redirect to login (except guest checkout)
    if (currentPath.split('/')[1].toUpperCase() !== 'CHECKOUTGUEST') {
      // Use window.location.href instead of router.navigate for reliability in error scenarios
      setTimeout(() => {
        window.location.href = '/login';
      }, 400);
    }
  }
}

/**
 * Check if status is 4xx (except 401) or 5xx
 */
function isServerOrClientError(status: number): boolean {
  const statusStr = status.toString();
  return statusStr.startsWith('5') || (statusStr.startsWith('4') && status !== 401);
}

/** Hostnames where error logging to backend is skipped (e.g. local dev). Add more as needed. */
const ERROR_LOG_SKIP_HOSTNAMES = ['localhost'];

/**
 * Log error to backend
 */
function logError(err: HttpErrorResponse, config: AuthInterceptorConfig, isBrowser: boolean): void {
  if (!isBrowser) return;
  const hostname = window.location.hostname;
  if (ERROR_LOG_SKIP_HOSTNAMES.includes(hostname)) return;

  const errorLogUrl = config.errorLogUrl || window.location.origin + '/way-admin/v1/error/public/log';
  const jsonData = {
    errorStatusCode: err.status || 500,
    errorModule: `JavaScript - ${config.appName} - ${err.status}`,
    errorMethodName: 'On Body Error',
    errorMethodInputName: 'browser ' + navigator.userAgent,
    errorSimpleMessage: JSON.stringify(err.error),
    errorStackTrace: location.href + ' ' + JSON.stringify(err)
  };

  const xhr = new XMLHttpRequest();
  xhr.open('POST', errorLogUrl);
  xhr.setRequestHeader('Content-type', 'application/json;charset=UTF-8');
  xhr.send(JSON.stringify(jsonData));
}
